
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";

import Icon from "@/components/UI/Icon.vue";
import OxyClassToken from "@/oxygen/classes/OxyClassToken.vue";
import { OxyNestedSelector, OxySelector } from "@/oxygen/classes/lib/types";
import draggable from "vuedraggable";
import defensiveCopy from "@/util/defensive-copy";
import { globalStore } from "@/store/store-accessor";
import SelectorsListItem from "@/oxygen/classes/panel/SelectorsListItem.vue";
import CollectionsInput from "@/oxygen/CollectionsInput.vue";
import AddSelector from "@/oxygen/classes/panel/AddSelector.vue";
import IconButton from "@/components/UI/IconButton.vue";
import { breakdanceUndo } from "@/lib/undo/undo";

@Component({
  components: {
    IconButton,
    AddSelector,
    CollectionsInput,
    Icon,
    draggable,
    TextInput,
    OxyClassToken,
    SelectorsListItem
  }
})
export default class SelectorsList extends Vue {
  @Prop()
  searchable: boolean | undefined;

  @Prop()
  selectors!: OxySelector[];

  searchString: string | null = "";

  dragOptions = {
    animation: 200,
    filter: ".oxy-class-token-actions__button",
    ghostClass: "breakdance-repeater-item--ghost",
    forceFallback: true
  };

  @Watch("selectors", {
    immediate: true,
    deep: true
  })
  onValueChange() {
    this.updateInternalValue();
    this.checkMissingCollections();
  }

  checkMissingCollections() {
    const currentCollections = [
      ...new Set(this.selectors.map(item => item.collection))
    ];

    const missingCollections = currentCollections.filter(
      item => !this.collections.includes(item)
    );

    if (missingCollections.length) {
      globalStore.setOxygenSelectorsCollections(currentCollections);
    }
  }

  selectorsByCollection: Record<string, OxySelector[]> = {};

  @Watch("collections", {
    immediate: true,
    deep: true
  })
  onColsChange() {
    this.updateInternalValue();
  }

  updateInternalValue() {
    this.collections.forEach(collection => {
      this.$set(
        this.selectorsByCollection,
        collection,
        this.getCollectionSelectors(collection)
      );
    });
  }

  get collections() {
    return globalStore.oxySelectorsCollections;
  }

  get filteredSelectors() {
    return this.selectors.filter(selector => {
      if (!this.searchString) return true;

      const nameMatches = selector.name
        .toLowerCase()
        .includes(this.searchString.toLowerCase());

      const childrenMatch = selector.children?.some(child =>
        child.name.includes(this.searchString!)
      );

      return nameMatches || childrenMatch;
    });
  }

  handleDrag(collectionSelectors: OxySelector[], collection: string) {
    this.selectorsByCollection[collection] = collectionSelectors.map(v => ({
      ...v,
      collection
    }));

    const updatedSelectors: OxySelector[] = [];

    this.collections.forEach(collection => {
      updatedSelectors.push(...this.selectorsByCollection[collection]);
    });

    this.setSelectors(updatedSelectors);
  }

  handleNestedDrag(parentId: string, newChildren: OxyNestedSelector[]) {
    const updatedSelectors = defensiveCopy(this.selectors).map(selector => {
      if (selector.id === parentId) {
        return {
          ...selector,
          children: newChildren
        };
      }
      return selector;
    });

    this.setSelectors(updatedSelectors);
  }

  setSelectors(selectors: OxySelector[]) {
    breakdanceUndo.transaction(() => {
      globalStore.setOxygenSelectors(defensiveCopy(selectors));
    }, "Update Selectors");
  }

  setCollections(collections: string[]) {
    breakdanceUndo.transaction(() => {
      globalStore.setOxygenSelectorsCollections(collections);
    }, "Update Selector Collections");
  }

  getCollectionSelectors(collection: string) {
    return this.selectors.filter(item => item.collection === collection);
  }

  isLocked(collection: string) {
    return this.selectors
      .filter(selector => selector.collection === collection)
      .every(selector => selector.locked);
  }

  toggleCollectionLock(collection: string) {
    const selectors = defensiveCopy(this.selectors);

    const collectionSelectors = selectors.filter(
      selector => selector.collection === collection
    );

    const lockAll = collectionSelectors.some(selector => !selector.locked);

    const newSelectors = selectors.map(selector => {
      if (selector.collection === collection) {
        selector.locked = lockAll;
      }
      return selector;
    });

    this.setSelectors(newSelectors);
  }
}
